import { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { cn } from '@/lib/utils';
import type { Block, DividerBlockContent } from '@/types';

interface DividerBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

function DividerBlock({ block, isEditable = false, onUpdate }: DividerBlockProps) {
  const content = block.content as DividerBlockContent;
  const [showColorPicker, setShowColorPicker] = useState(false);

  const style = content.style || 'solid';
  const color = content.color || '#e0e0e0';
  const thickness = content.thickness || 1;

  if (isEditable) {
    return (
      <div>
        <div className="flex gap-4 mb-4 p-2 bg-gray-100 rounded items-center flex-wrap">
          {/* Style selector */}
          <div className="inline-flex rounded-md border border-input">
            {([
              { value: 'solid' as const, render: <div className="w-[30px] h-0.5 bg-current" /> },
              { value: 'dashed' as const, render: <div className="w-[30px] h-0 border-t-2 border-dashed border-current" /> },
              { value: 'dotted' as const, render: <div className="w-[30px] h-0 border-t-2 border-dotted border-current" /> },
            ]).map(({ value, render }) => (
              <button
                key={value}
                className={cn(
                  'inline-flex items-center justify-center h-8 w-10 transition-colors',
                  style === value
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                )}
                onClick={() => {
                  onUpdate?.({ content: { ...content, style: value } });
                }}
              >
                {render}
              </button>
            ))}
          </div>

          {/* Color picker */}
          <div className="relative">
            <div
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="w-8 h-8 border-2 border-gray-300 rounded cursor-pointer"
              style={{ backgroundColor: color }}
            />
            {showColorPicker && (
              <div className="absolute top-full left-0 mt-2 z-10 bg-white p-4 rounded shadow-lg">
                <HexColorPicker
                  color={color}
                  onChange={(newColor) =>
                    onUpdate?.({ content: { ...content, color: newColor } })
                  }
                />
                <div className="flex gap-1 mt-2 flex-wrap">
                  {['#e0e0e0', '#9e9e9e', '#424242', '#000000', '#1976d2', '#388e3c', '#f57c00'].map(
                    (c) => (
                      <div
                        key={c}
                        onClick={() => {
                          onUpdate?.({ content: { ...content, color: c } });
                          setShowColorPicker(false);
                        }}
                        className="w-5 h-5 rounded-sm cursor-pointer border border-border"
                        style={{ backgroundColor: c }}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Thickness slider */}
          <div className="flex items-center gap-2 min-w-[150px]">
            <span className="text-xs text-muted-foreground">Dikte:</span>
            <input
              type="range"
              min={1}
              max={8}
              value={thickness}
              onChange={(e) => {
                onUpdate?.({ content: { ...content, thickness: Number(e.target.value) } });
              }}
              className="w-20 accent-primary"
            />
            <span className="text-xs">{thickness}px</span>
          </div>
        </div>

        <div
          className="w-full"
          style={{ borderTop: `${thickness}px ${style} ${color}` }}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full my-4"
      style={{ borderTop: `${thickness}px ${style} ${color}` }}
    />
  );
}

export default DividerBlock;
